import { useEffect, useState } from "react";
import { LogIn, LogOut, Clock, CalendarDays, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, PageTitle, Stat } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const hora = (iso) => (iso
  ? new Date(iso).toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" })
  : "—");
const dia = (f) => new Date(`${f}T12:00:00`).toLocaleDateString("es-DO",
  { weekday: "short", day: "2-digit", month: "short" });

export default function DocentePonche() {
  const [datos, setDatos] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const cargar = () => api.get("/ponches/mios").then((r) => setDatos(r.data))
    .catch(() => setDatos({ empleado: null, hoy: [], marcas: [], horas_mes: 0 }));

  useEffect(() => { cargar(); }, []);

  const ponchar = async (tipo) => {
    setEnviando(true);
    try {
      const { data } = await api.post("/ponches", { tipo });
      toast.success(tipo === "entrada"
        ? `Entrada registrada a las ${hora(data.ponche.entrada)}`
        : `Salida registrada a las ${hora(data.ponche.salida)} · ${data.horas} hora(s)`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
    setEnviando(false);
  };

  if (!datos) return <Loading />;
  if (!datos.empleado) {
    return (
      <div data-testid="docente-ponche">
        <PageTitle title="Ponchador"
                   subtitle="Marque su entrada y su salida desde su portal." />
        <div className="rounded-lg border border-border/60 bg-card p-10 text-center"
             data-testid="ponche-sin-ficha">
          <Clock className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {datos.aviso || "Su cuenta aún no está vinculada a una ficha de empleado en Nómina. "
              + "Pida a administración que la registre para poder ponchar."}
          </p>
        </div>
      </div>
    );
  }
  const abierto = datos.abierto;

  return (
    <div data-testid="docente-ponche">
      <PageTitle title="Ponchador"
                 subtitle="Marque su entrada y su salida. La hora la registra el sistema (hora de República Dominicana)." />

      <div className="mb-8 grid gap-4 sm:grid-cols-4">
        <Stat label="Estado de hoy" value={abierto ? "En jornada" : "Fuera de jornada"}
              icon={Clock} testId="ponche-estado" />
        <Stat label="Marcas del mes" value={datos.marcas.length} icon={CalendarDays}
              testId="ponche-marcas-mes" />
        <Stat label="Horas del mes" value={`${datos.horas_mes} h`} icon={Clock}
              testId="ponche-horas-mes" />
        <Stat label="Tardanzas del mes" value={datos.tardanzas || 0} icon={AlertTriangle}
              tone={datos.tardanzas ? "warn" : "default"} testId="ponche-tardanzas" />
      </div>

      {datos.empleado?.hora_entrada && (
        <p className="mb-6 text-sm text-muted-foreground" data-testid="ponche-horario">
          Su horario pactado: entrada <b>{datos.empleado.hora_entrada}</b>
          {datos.empleado.hora_salida ? ` · salida ${datos.empleado.hora_salida}` : ""}
          {" "}(tolerancia {datos.empleado.tolerancia_minutos ?? 0} min)
        </p>
      )}

      <div className="mb-8 flex flex-col gap-4 rounded-lg border border-border/60 bg-card p-6 sm:flex-row">
        <Button className="flex-1 rounded-full py-8 text-base" disabled={enviando || !!abierto}
                onClick={() => ponchar("entrada")} data-testid="ponchar-entrada">
          <LogIn className="mr-2 h-5 w-5" /> Ponchar entrada
        </Button>
        <Button variant="outline" className="flex-1 rounded-full py-8 text-base"
                disabled={enviando || !abierto} onClick={() => ponchar("salida")}
                data-testid="ponchar-salida">
          <LogOut className="mr-2 h-5 w-5" /> Ponchar salida
        </Button>
      </div>

      {abierto && (
        <p className="mb-6 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm"
           data-testid="ponche-abierto">
          Entrada abierta desde las <b>{hora(abierto.entrada)}</b>. Recuerde ponchar la salida.
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[460px] text-sm" data-testid="tabla-mis-ponches">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Día</th>
              <th className="px-6 py-3">Entrada</th>
              <th className="px-6 py-3">Salida</th>
              <th className="px-6 py-3">Horas</th>
            </tr>
          </thead>
          <tbody>
            {datos.marcas.map((m) => (
              <tr key={m.id} className={`border-t border-border/60 ${m.tarde ? "bg-destructive/5" : ""}`}>
                <td className="px-6 py-3 capitalize">{dia(m.fecha)}</td>
                <td className="px-6 py-3 font-mono text-xs">
                  {m.tarde ? (
                    <span className="font-semibold text-destructive" data-testid={`mi-tardanza-${m.id}`}>
                      {hora(m.entrada)} · {m.minutos_tarde} min tarde
                    </span>
                  ) : hora(m.entrada)}
                </td>
                <td className="px-6 py-3 font-mono text-xs">
                  {m.salida ? hora(m.salida) : <Badge variant="outline">sin salida</Badge>}
                </td>
                <td className="px-6 py-3 font-mono text-xs">{m.horas ? `${m.horas} h` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {datos.marcas.length === 0 && (
          <p className="p-10 text-center text-sm text-muted-foreground" data-testid="ponches-vacios">
            Todavía no tiene marcas este mes.
          </p>
        )}
      </div>
    </div>
  );
}
